import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cardImg from 'assets/img/generic/card.png';
import transparentImg from 'assets/img/generic/default.png';
import darkModeImg from 'assets/img/generic/falcon-mode-dark.jpg';
import defaultModeImg from 'assets/img/generic/falcon-mode-default.jpg';
import invertedImg from 'assets/img/generic/inverted.png';
import vibrantImg from 'assets/img/generic/vibrant.png';
import arrowsH from 'assets/img/icons/arrows-h.svg';
import leftArrowFromLeft from 'assets/img/icons/left-arrow-from-left.svg';
import Flex from 'components/common/Flex';
import SoftBadge from 'components/common/SoftBadge';
import useApplication from 'hooks/useApplication';
import React, { useMemo } from 'react';
import { Button, ButtonGroup, Form, Offcanvas } from 'react-bootstrap';
import RadioItem from '../forms/RadioItem';

const DisplaySettingsPanel = () => {
  const {
    application: {
      isFluid,
      isRTL,
      isDark,
      navbarPosition,
      navbarStyle,
      showSettingPanel,
      disabledNavbarPosition
    },
    setApplication,
    applicationDispatch
  } = useApplication();

  // Use useMemo to compute navbar styles based on isDark
  const navbars = useMemo(
    () => [
      {
        name: 'card',
        image: isDark ? invertedImg : cardImg
      },
      {
        name: 'inverted',
        image: isDark ? cardImg : invertedImg
      },
      {
        name: 'vibrant',
        image: vibrantImg
      },
      {
        name: 'transparent',
        image: transparentImg
      }
    ],
    [isDark]
  );

  return (
    <Offcanvas
      show={showSettingPanel}
      onHide={() => setApplication('showSettingPanel', false)}
      placement='end'
      style={{ maxWidth: '22rem' }}
      className='border-0'>
      <Offcanvas.Header
        closeButton
        className='settings-panel-header px-3 bg-light'>
        <Offcanvas.Title as='div' className='py-1 px-0 z-index-1'>
          <div className='d-flex justify-content-between align-items-center mb-0'>
            <h5 className='fs-0'>
              <FontAwesomeIcon
                icon={['fas', 'clover']}
                transform='rotate-45 grow-10'
                className='ms-1 me-2 fs-0 text-pastel-green-dark'
              />
              Display Settings
            </h5>
          </div>
          <Button
            variant='primary'
            size='sm'
            className='rounded-pill mt-0 mb-0 ms-0 fs--2'
            onClick={() => {
              applicationDispatch({ type: 'RESET' });
            }}>
            <FontAwesomeIcon
              icon='redo-alt'
              style={{ fontSize: '10px' }}
              className='me-1'
            />
            Reset
          </Button>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className='scrollbar'>
        <h5 className='fs--1'>
          <FontAwesomeIcon icon='palette' className='me-2' />
          Color Scheme
        </h5>
        <p className='fs--2 mb-2'>
          Choose the perfect color mode for your eyes.
        </p>

        <ButtonGroup className='btn-group-navbar-style mb-0'>
          <RadioItem
            name='theme-mode'
            label='light'
            active={!isDark}
            onChange={({ target }) => setApplication('isDark', !target.checked)}
            image={defaultModeImg}
          />
          <RadioItem
            name='theme-mode'
            label='dark'
            active={isDark}
            onChange={({ target }) => setApplication('isDark', target.checked)}
            image={darkModeImg}
          />
        </ButtonGroup>

        <hr className='mt-2' />

        <Flex>
          <div>
            <Flex alignItems='center' tag='h5' className='fs--1 fw-light'>
              <FontAwesomeIcon icon='bars' className='me-2' />
              Menu Position
              <SoftBadge bg='alert' pill className='fs--2 ms-2'>
                New
              </SoftBadge>
            </Flex>
            <p className='fs--2 fw-light mb-2'>
              Choose between vertical, top, combo, or double-top navbar position
              to suit your needs.
            </p>
            <Form.Select
              className='mb-3 fs--2'
              size='sm'
              defaultValue={navbarPosition}
              onChange={({ target }) =>
                setApplication('navbarPosition', target.value)
              }>
              {['vertical', 'top', 'combo', 'double-top'].map(option => (
                <option
                  key={option}
                  disabled={disabledNavbarPosition.includes(option)}
                  value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </option>
              ))}
            </Form.Select>
          </div>
        </Flex>
        <hr className='mt-1' />

        <h5 className='fs--1'>
          <FontAwesomeIcon icon='ellipsis-vertical' className='me-2 ms-1' />
          Vertical Navbar Style
        </h5>
        <p className='fs--2 fw-light mb-2'>
          Switch between styles for your vertical navbar
        </p>
        <ButtonGroup className='btn-group-navbar-style'>
          {navbars.slice(0, 2).map(item => (
            <RadioItem
              key={item.name}
              name='navbar-style'
              label={item.name}
              className='fw-normal'
              active={navbarStyle === item.name}
              onChange={() => setApplication('navbarStyle', item.name)}
              image={item.image}
            />
          ))}
        </ButtonGroup>
        <ButtonGroup className='btn-group-navbar-style'>
          {navbars.slice(2, 4).map(item => (
            <RadioItem
              key={item.name}
              name='navbar-style'
              label={item.name}
              className='fw-normal'
              active={navbarStyle === item.name}
              onChange={() => setApplication('navbarStyle', item.name)}
              image={item.image}
            />
          ))}
        </ButtonGroup>
        <hr className='mt-2' />

        <Flex justifyContent='between'>
          <img src={arrowsH} alt='' width={20} className='me-2 h-100' />
          <div className='flex-1'>
            <h5 className='fs--1'>Fluid Layout</h5>
            <p className='fs--2 mb-0 fw-light'>
              Toggle fluid or fixed-width layout
            </p>
          </div>
          <Form.Check
            type='switch'
            id='fluid-mode-switch'
            checked={isFluid}
            onChange={({ target }) => setApplication('isFluid', target.checked)}
          />
        </Flex>
        <hr />

        <Flex justifyContent='between'>
          <img
            src={leftArrowFromLeft}
            alt=''
            width={20}
            className='me-2 h-100'
          />
          <div className='flex-1'>
            <h5 className='fs--1'>RTL Mode</h5>
            <p className='fs--2 mb-0 fw-light'>
              Switch your language direction{' '}
            </p>
          </div>
          <Form.Check
            type='switch'
            id='rtl-switch'
            checked={isRTL}
            onChange={({ target }) => setApplication('isRTL', target.checked)}
          />
        </Flex>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default DisplaySettingsPanel;
